import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronUp, Search, ShoppingCart, SquarePlay } from 'lucide-react'
import { useProductStore } from '../store/productStore'
import { useAuth } from '../AuthContext'
import { useFavorites } from '../hooks/useFavorites'
import { useMediaQuery, usePrefersReducedMotion } from '../hooks/useMediaQuery'
import { useCatalogFilters } from '../hooks/useCatalogFilters'
import CatalogTopbar from '../features/catalog/CatalogTopbar'
import MobileMenuDrawer from '../features/catalog/MobileMenuDrawer'
import VirtualGrid, { type VirtualGridHandle } from '../features/catalog/VirtualGrid'
import ProductCard from '../features/catalog/ProductCard'
import FavoritesDrawer from './FavoritesDrawer'
import CatalogSkeleton from '../layout/skeletons/CatalogSkeleton'
import { useCategoryStore } from '../store/categoryStore'
import HeroBanner from '../features/catalog/HeroBanner'
import { useBannerState } from '../hooks/useBannerState'

export default function CatalogPage() {
  const { products, isLoading } = useProductStore()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const { favoriteIds, isFavorite, toggle: toggleFavorite, clear: clearFavorites, count: favCount } = useFavorites()
  const prefersReducedMotion = usePrefersReducedMotion()
  const filters = useCatalogFilters()
  const { fetchCategories } = useCategoryStore()

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const [navVisible, setNavVisible] = useState(true)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [favDrawerOpen, setFavDrawerOpen] = useState(false)
  const [virtualScrolled, setVirtualScrolled] = useState(false)
  const [virtualKey, setVirtualKey] = useState('with-banner')

  const { visible: bannerVisible, isPlayBtnVisible, handleReplay, handleClosePermanent: handleCloseBannerPermanent } = useBannerState(() => {
    setTimeout(() => setVirtualKey('no-banner'), 0)
  })

  const lastScrollY  = useRef(0)
  const searchRef    = useRef<HTMLInputElement>(null)
  const virtualGridRef = useRef<VirtualGridHandle>(null)

  const isMobile  = useMediaQuery('(max-width: 639px)')
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const isLandscape = !isMobile && !isDesktop;

  useEffect(() => {
    // INSTANT scroll to top - no animation
    window.scrollTo(0, 0);
  }, []);

  // Hide nav on scroll down
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setNavVisible(y < lastScrollY.current || y < 60)
      lastScrollY.current = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Shift → focus search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      if (e.key === 'Shift') { e.preventDefault(); searchRef.current?.focus() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuOpen && !(e.target as Element).closest('[data-user-menu]'))
        setUserMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [userMenuOpen])

  const favoriteProducts = useMemo(
    () => products.filter(p => favoriteIds.includes(p.id)),
    [products, favoriteIds]
  )
  

  const FIT_MEDIA_VIRTUALIZE_THRESHOLD = isMobile || isLandscape ? 2 : 5;
  const useVirtual = filters.filtered.length >= FIT_MEDIA_VIRTUALIZE_THRESHOLD

  const handleEmail = () => {
    const subject = encodeURIComponent("Subject of the email");
    const body = encodeURIComponent("Body of the email");
    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=ehrlichdaniel1@gmail.com&su=${subject}&body=${body}`;
    window.open(gmailLink, '_blank');
  }

  const handleCloseBanner = () => {
    setTimeout(() => setVirtualKey('no-banner'), 0)
  }


  return (
    <div dir="rtl" className="min-h-screen bg-[linear-gradient(135deg,#FAF7F2_0%,#FAF7F2_70%,#FAF7F2_100%)]">
      <div className="page-bg" />

      <CatalogTopbar
        navVisible={navVisible}
        user={user}
        onLogout={signOut}
        search={filters.search}
        setSearch={filters.setSearch}
        searchRef={searchRef}
        userMenuOpen={userMenuOpen}
        setUserMenuOpen={setUserMenuOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        activeFilterCount={filters.activeFilterCount}
        filtered={filters.filtered}
        selectedCategory={filters.selectedCategory}
        setSelectedCategory={filters.setSelectedCategory}
        counts={filters.counts}
        prefersReducedMotion={prefersReducedMotion}
        statusFilter={filters.statusFilter}
        setStatusFilter={filters.setStatusFilter}
        inStockOnly={filters.inStockOnly}
        setInStockOnly={filters.setInStockOnly}
        priceMin={filters.priceMin}
        setPriceMin={filters.setPriceMin}
        priceMax={filters.priceMax}
        setPriceMax={filters.setPriceMax}
        sortBy={filters.sortBy}
        setSortBy={filters.setSortBy}
        clearFilters={filters.clearFilters}
        rootCategories={filters.rootCategories}
        selectedShortcut={filters.selectedShortcut}
        setSelectedShortcut={filters.setSelectedShortcut}
        filteredCount={filters.filtered.length}
        selectedCategoryName={filters.selectedCategory !== 'all' 
          ? filters.rootCategories.find(c => c.id === filters.selectedCategory)?.name 
          : undefined}
        onBannerClose={() => setTimeout(() => setVirtualKey('no-banner'), 0)}
      />

      <MobileMenuDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        userName={user?.name}
        userEmail={user?.email}
      />


      {/* ── CONTENT ─────────────────────────────────────────────────────── */}
      <main className="relative z-10 max-w-7xl mx-auto px-2 pt-[2px] lg:pt-[0px] mb-0 lg:pb-0">
        {(!isLandscape || !virtualScrolled) && (
          <div className="flex flex-row sm:hidden items-basline justify-between">
            <div className="mb-0 flex flex-row sm:hidden px-4 gap-4 -mt-1 lg:-mt-2 pb-2">
              <h1 className="font-serif text-[24px] font-bold text-[#1A1A1A] tracking-tight leading-none mb-1">
                קטלוג מוצרים
              </h1>
              <p className="text-sm text-[#6E6660] pt-1" aria-live="polite">
                {filters.filtered.length} מוצרים
              </p>
            </div>

            <div className="-mt-1">
              <motion.button 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, delay: 0.5 }}
                style={{ opacity: 0 }}
                className={`px-2 py-1 mx-1.5 rounded-sm cursor-pointer ${isPlayBtnVisible ? 'visible' : 'hidden'}`}
                onClick={handleReplay}>
                <SquarePlay className='h-6.5 w-6.5 text-green-primary stroke-1' />
              </motion.button>
            </div>
          </div>
        )}

        {/* ── HERO BANNER ─────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-2">
          <HeroBanner 
            visible={bannerVisible} 
            onClose={handleCloseBanner} 
            onClosePermanent={handleCloseBannerPermanent} 
          />
        </div>

        {isLoading && <CatalogSkeleton />}

        {!isLoading && filters.filtered.length === 0 && products.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            className="text-center py-20 text-[#6E6660]"
          >
            <div className="w-full flex items-center justify-center mb-8" aria-hidden="true">
              <Search className="h-8 w-8" />
            </div>
            <p className="text-base font-semibold">לא נמצאו מוצרים</p>
          </motion.div>
        )}

        {!isLoading && filters.filtered.length > 0 && useVirtual && (
          <section aria-label="רשת מוצרים">
            <VirtualGrid
              key={virtualKey}
              ref={virtualGridRef}
              items={filters.filtered}
              onClickItem={id => navigate(`/product/${id}`)}
              isFavorite={isFavorite}
              onFavoriteToggle={(id, e) => { e.stopPropagation(); toggleFavorite(id) }}
              onScrollChange={setVirtualScrolled}
            />
          </section>
        )}

        {!isLoading && filters.filtered.length > 0 && !useVirtual && (
          <section aria-label="רשת מוצרים" className='pt-3'>
            <motion.div layout className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-5">
              <AnimatePresence mode="popLayout">
                {filters.filtered.map((product, i) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24, scale: prefersReducedMotion ? 1 : 0.97 }}
                    animate={{
                      opacity: 1, y: 0, scale: 1,
                      transition: prefersReducedMotion
                        ? { duration: 0.15 }
                        : { delay: i * 0.05, duration: 0.4, ease: [0.16, 1, 0.3, 1] },
                    }}
                    exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -12, scale: prefersReducedMotion ? 1 : 0.97, transition: { duration: 0.2 } }}
                    layout
                    className={`pb-[5px] ${filters.filtered.length === 1 ? 'pt-0.5 lg:pt-0 px-3.5 lg:px-0' : ''}`}
                    whileHover={prefersReducedMotion ? {} : { y: -5 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <ProductCard
                      product={product}
                      index={i}
                      onClick={() => navigate(`/product/${product.id}`)}
                      isFavorite={isFavorite(product.id)}
                      onFavoriteToggle={e => { e.stopPropagation(); toggleFavorite(product.id) }}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </section>
        )}
        {!isLoading && filters.filtered.length > 0 && useVirtual && (
          <footer className="text-center pt-4 pb-8.5">  
            <a 
              href="mailto:ehrlichdaniel1@gmail.com"
              target="_blank" rel="noopener noreferrer"
              className="text-[16px] text-[#A89F97] hover:text-[#6B6560] transition-colors"
              onClick={(e) => { e.preventDefault(); handleEmail() }}>
              נבנה ע״י Daniel Ehrlich
            </a>
          </footer>
        )}

      </main>
      {/* ── FLOATING FAVORITES BUTTON ─────────────────────────────────── */}
      <AnimatePresence>
        {favCount > 0 && (
          <motion.button
          key="fab"
          initial={{ scale: prefersReducedMotion ? 1 : 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: prefersReducedMotion ? 1 : 0, opacity: 0 }}
          whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
          whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          onClick={() => setFavDrawerOpen(true)}
          aria-label={`פתח מועדפים - ${favCount} מוצרים`}
          className={`absolute top-2.5 md:top-17 lg:top-17 left-36
            ${filters.activeFilterCount === 0 
              ? 'md:left-36 lg:left-77' : 'md:left-43 lg:left-64'} 
            md:z-60 z-45 lg:z-50 flex items-center
            gap-2 sm:gap-3 pl-3 sm:pl-4 pr-4 sm:pr-5 py-2
            bg-[#F2EDE4] hover:bg-[#EAE4DB] text-[#6B6560] rounded-2xl
            border border-0.5 border-[#508169] cursor-pointer hover:shadow-[0_4px_12px_rgba(45,106,79,0.28)] transition-[box-shadow] duration-200
            font-semibold text-[13px] shadow-[0_8px_24px_rgba(26,26,16,0.125)] transition-500`}
            >
              רשימת מוצרים
            <div className="relative">
              <ShoppingCart className="w-3.5 h-3.5 text-[#2D6A4F]" />
              <span className="absolute -top-4.5 lg:-top-5 -right-30
                              py-1 px-0.5 lg:py-1 lg:px-1 rounded-full min-w-[18px]
                              bg-[#D8F3DC] border border-[#2D6A4F] text-[#2D6A4F] text-[9px]
                              flex items-center justify-center leading-none">
                {favCount}
              </span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── GO TO TOP ──────────────────────────────────────────────────── */}
      {useVirtual && virtualScrolled && (
        <motion.button
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          onClick={() => virtualGridRef.current?.scrollToTop()}
          className="fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full bg-green-primary text-white border-none cursor-pointer shadow-[0_4px_14px_rgba(45,106,79,0.35)] flex items-center justify-center hover:brightness-110 transition-[filter] duration-500"
          aria-label="חזרה למעלה"
        >
          <ChevronUp className="h-5 w-5" />
        </motion.button>
      )}

      {/* ── FAVORITES DRAWER ───────────────────────────────────────────── */}
      <FavoritesDrawer
        open={favDrawerOpen}
        onClose={() => setFavDrawerOpen(false)}
        products={favoriteProducts}
        onRemove={toggleFavorite}
        onClearAll={clearFavorites}
        onNavigate={id => navigate(`/product/${id}`)}
      />
    </div>
  )
}